// FILE: CollegeGitHubDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Github,
  RefreshCw,
  ExternalLink,
  GitPullRequest,
  Users,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  UserPlus,
  UserMinus,
  ChevronDown,
  ChevronUp,
  Loader2,
  RotateCcw,
  Shield,
  GitBranch,
  Activity,
} from 'lucide-react';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';
import CollegeFooter from './CollegeFooter';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.theskillgenie.com';

/* ── helpers ── */
const fmt = (v) => (v == null ? '—' : v);
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const STATUS_META = {
  SUCCESS:    { label: 'Active',      color: '#059669', bg: '#D1FAE5', Icon: CheckCircle2 },
  PROCESSING: { label: 'Processing',  color: '#D97706', bg: '#FEF3C7', Icon: Clock },
  PENDING:    { label: 'Pending',     color: '#6366F1', bg: '#EEF2FF', Icon: Clock },
  FAILED:     { label: 'Failed',      color: '#DC2626', bg: '#FEE2E2', Icon: XCircle },
  NOT_SET:    { label: 'Not Set Up',  color: '#9CA3AF', bg: '#F9FAFB', Icon: AlertCircle },
};

const INVITE_META = {
  READY_FOR_INVITE: { label: 'Ready',    color: '#6366F1', bg: '#EEF2FF' },
  INVITED:          { label: 'Invited',  color: '#D97706', bg: '#FEF3C7' },
  ACCEPTED:         { label: 'Accepted', color: '#059669', bg: '#D1FAE5' },
  DECLINED:         { label: 'Declined', color: '#DC2626', bg: '#FEE2E2' },
  REVOKED:          { label: 'Revoked',  color: '#9CA3AF', bg: '#F3F4F6' },
  SUSPENDED:        { label: 'Suspended',color: '#92400E', bg: '#FEF3C7' },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.NOT_SET;
  const { Icon } = meta;
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ color: meta.color, background: meta.bg }}
    >
      <Icon size={11} />
      {meta.label}
    </span>
  );
};

const InviteBadge = ({ status }) => {
  const meta = INVITE_META[status] || { label: status || 'Unknown', color: '#6B7280', bg: '#F3F4F6' };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ color: meta.color, background: meta.bg }}
    >
      {meta.label}
    </span>
  );
};

/* ── Confirm dialog ── */
const ConfirmDialog = ({ open, title, message, onConfirm, onCancel, loading, confirmLabel = 'Confirm', danger = false }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
        <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-6">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} disabled={loading} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 transition">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition flex items-center justify-center gap-2 ${danger ? 'bg-red-500 hover:bg-red-600' : 'bg-indigo-600 hover:bg-indigo-700'}`}
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Stat card ── */
const Stat = ({ label, value, sub, icon: Icon, color = '#6366F1' }) => (
  <div className="bg-white rounded-2xl p-4 flex items-center gap-4" style={{ border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-xs)' }}>
    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
      <Icon size={18} style={{ color }} />
    </div>
    <div className="min-w-0">
      <div className="text-xl font-bold text-gray-900 leading-none">{fmt(value)}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════════════════ */
const CollegeGitHubDashboard = () => {
  const [collegeId, setCollegeId] = useState(null);
  const [tenantId,  setTenantId]  = useState(null);

  // Data
  const [repo,      setRepo]      = useState(null);   // GET /api/github/repo
  const [students,  setStudents]  = useState([]);     // GET /api/github/students
  const [analytics, setAnalytics] = useState(null);  // GET /api/github/analytics

  // Loading & error states
  const [loadingRepo,      setLoadingRepo]      = useState(false);
  const [loadingStudents,  setLoadingStudents]  = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingProvision, setLoadingProvision] = useState(false);
  const [loadingSyncInv,   setLoadingSyncInv]   = useState(false);
  const [error,            setError]            = useState('');
  const [toast,            setToast]            = useState('');

  // UI state
  const [studentsExpanded, setStudentsExpanded] = useState(true);
  const [prsExpanded,      setPrsExpanded]      = useState(true);

  // Per-student action loading map: { studentId: 'invite' | 'revoke' | null }
  const [studentActions, setStudentActions] = useState({});

  // Confirm dialog
  const [confirm, setConfirm] = useState(null); // { title, message, onConfirm, danger, loading }

  /* ── init ── */
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const cid = user?.id || user?.college_id;
    const tid = user?.tenant_id || user?.tenantId;
    if (cid) { setCollegeId(cid); }
    if (tid) { setTenantId(tid); }
  }, []);

  /* ── fetch helpers ── */
  const fetchRepo = useCallback(async (cid) => {
    if (!cid) return;
    setLoadingRepo(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/github/repo`, { params: { collegeId: cid } });
      setRepo(res.data?.repo || res.data || null);
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      if (err.response?.status !== 404) setError(`Repository: ${msg}`);
      setRepo(null);
    } finally {
      setLoadingRepo(false);
    }
  }, []);

  const fetchStudents = useCallback(async (cid) => {
    if (!cid) return;
    setLoadingStudents(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/github/students`, { params: { collegeId: cid } });
      setStudents(res.data?.students || []);
    } catch {
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async (cid) => {
    if (!cid) return;
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/github/analytics`, { params: { collegeId: cid } });
      setAnalytics(res.data?.analytics || res.data || null);
    } catch {
      setAnalytics(null);
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  useEffect(() => {
    if (collegeId) {
      fetchRepo(collegeId);
      fetchStudents(collegeId);
      fetchAnalytics(collegeId);
    }
  }, [collegeId, fetchRepo, fetchStudents, fetchAnalytics]);

  /* ── toast helper ── */
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  /* ── provision / retry ── */
  const handleProvision = async () => {
    if (!collegeId) return;
    setLoadingProvision(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/github/provision`, { collegeId, tenantId });
      showToast(res.data?.message || 'Repository provisioning started.');
      setTimeout(() => fetchRepo(collegeId), 2000);
    } catch (err) {
      showToast(err.response?.data?.error || 'Provisioning failed.');
    } finally {
      setLoadingProvision(false);
    }
  };

  /* ── sync invites ── */
  const handleSyncInvites = async () => {
    if (!collegeId) return;
    setLoadingSyncInv(true);
    try {
      await axios.post(`${API_BASE_URL}/api/github/sync-invites`, { collegeId });
      showToast('Invitation statuses synced.');
      fetchStudents(collegeId);
    } catch (err) {
      showToast(err.response?.data?.error || 'Sync failed.');
    } finally {
      setLoadingSyncInv(false);
    }
  };

  /* ── invite student ── */
  const handleInvite = (student) => {
    setConfirm({
      title: 'Invite to Repository',
      message: `Send a GitHub repository invitation to ${student.full_name || student.email}?`,
      danger: false,
      confirmLabel: 'Send Invite',
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, loading: true }));
        setStudentActions((prev) => ({ ...prev, [student.student_id]: 'invite' }));
        try {
          await axios.post(`${API_BASE_URL}/api/github/students/${student.student_id}/invite`, {
            collegeId,
          });
          showToast(`Invitation sent to ${student.full_name || student.email}.`);
          fetchStudents(collegeId);
        } catch (err) {
          showToast(err.response?.data?.error || 'Invite failed.');
        } finally {
          setStudentActions((prev) => ({ ...prev, [student.student_id]: null }));
          setConfirm(null);
        }
      },
    });
  };

  /* ── revoke access ── */
  const handleRevoke = (student) => {
    setConfirm({
      title: 'Revoke Repository Access',
      message: `Remove ${student.full_name || student.email}'s access to the repository? This cannot be undone.`,
      danger: true,
      confirmLabel: 'Revoke Access',
      onConfirm: async () => {
        setConfirm((c) => ({ ...c, loading: true }));
        setStudentActions((prev) => ({ ...prev, [student.student_id]: 'revoke' }));
        try {
          await axios.delete(`${API_BASE_URL}/api/github/students/${student.student_id}/access`, {
            data: { collegeId, reason: 'admin_revoked' },
          });
          showToast(`Access revoked for ${student.full_name || student.email}.`);
          fetchStudents(collegeId);
        } catch (err) {
          showToast(err.response?.data?.error || 'Revoke failed.');
        } finally {
          setStudentActions((prev) => ({ ...prev, [student.student_id]: null }));
          setConfirm(null);
        }
      },
    });
  };

  /* ── derived ── */
  const provStatus = repo?.github_provisioning_status || (repo?.repo_url ? 'SUCCESS' : 'NOT_SET');
  const repoUrl    = repo?.repo_url || repo?.repoUrl;

  const invited  = students.filter((s) => s.invite_status === 'INVITED').length;
  const accepted = students.filter((s) => s.invite_status === 'ACCEPTED').length;
  const ready    = students.filter((s) => s.invite_status === 'READY_FOR_INVITE').length;

  const openPRs   = analytics?.openPullRequests   ?? analytics?.open_pull_requests   ?? 0;
  const mergedPRs = analytics?.mergedPullRequests ?? analytics?.merged_pull_requests ?? 0;
  const prs       = analytics?.pullRequests       ?? analytics?.pull_requests        ?? [];

  /* ── render ── */
  const isSetUp = provStatus === 'SUCCESS';

  return (
    <div className="college-main-layout" style={{ background: '#F8FAFC' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        {/* ── Main content ── */}
        <main className="college-main-content p-6">
          {/* ── Page header ── */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: '#EEF2FF' }}>
                <Github size={20} style={{ color: '#4F46E5' }} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 leading-tight">GitHub Collaboration</h1>
                <p className="text-sm text-gray-500">Manage your college's GitHub repository and student access</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => { fetchRepo(collegeId); fetchStudents(collegeId); fetchAnalytics(collegeId); }}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 bg-white border hover:bg-gray-50 transition"
                style={{ border: '1px solid var(--border-subtle)' }}
              >
                <RefreshCw size={14} className={loadingRepo ? 'animate-spin' : ''} />
                Refresh
              </button>

              {!isSetUp && (
                <button
                  onClick={handleProvision}
                  disabled={loadingProvision || provStatus === 'PROCESSING'}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 transition"
                >
                  {loadingProvision ? <Loader2 size={14} className="animate-spin" /> : <Github size={14} />}
                  {provStatus === 'PROCESSING' ? 'Setting Up…' : 'Set Up GitHub'}
                </button>
              )}

              {isSetUp && provStatus === 'FAILED' && (
                <button
                  onClick={handleProvision}
                  disabled={loadingProvision}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-60 transition"
                >
                  {loadingProvision ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                  Retry Setup
                </button>
              )}

              {repoUrl && (
                <a
                  href={repoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-gray-800 hover:bg-gray-900 transition"
                >
                  <ExternalLink size={14} />
                  Open Repository
                </a>
              )}
            </div>
          </div>

          {/* ── Error banner ── */}
          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2 text-sm text-red-700">
              <AlertCircle size={15} />
              {error}
            </div>
          )}

          {/* ── Toast ── */}
          {toast && (
            <div className="fixed bottom-6 right-6 z-[300] px-4 py-3 bg-gray-900 text-white text-sm rounded-xl shadow-lg animate-fadeIn">
              {toast}
            </div>
          )}

          {/* ── Repository Info Card ── */}
          <div className="bg-white rounded-2xl p-5 mb-5" style={{ border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-xs)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <GitBranch size={16} style={{ color: '#6366F1' }} />
                <h2 className="font-bold text-gray-900">Repository Details</h2>
              </div>
              {loadingRepo && <Loader2 size={14} className="animate-spin text-indigo-400" />}
            </div>

            {loadingRepo && !repo ? (
              <div className="text-sm text-gray-400 py-4 text-center">Loading repository info…</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Status</div>
                  <StatusBadge status={provStatus} />
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Repository Name</div>
                  <div className="text-sm font-semibold text-gray-800">{fmt(repo?.repo_name || repo?.repoName)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Visibility</div>
                  <div className="text-sm font-semibold text-gray-800 capitalize">{fmt(repo?.visibility) || 'Private'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Repository URL</div>
                  {repoUrl ? (
                    <a href={repoUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-indigo-600 hover:underline flex items-center gap-1 truncate max-w-[220px]">
                      <span className="truncate">{repoUrl}</span>
                      <ExternalLink size={11} className="flex-shrink-0" />
                    </a>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Created</div>
                  <div className="text-sm font-semibold text-gray-800">{fmtDate(repo?.created_at || repo?.createdAt)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-0.5">Provisioned</div>
                  <div className="text-sm font-semibold text-gray-800">{fmtDate(repo?.github_provisioned_at)}</div>
                </div>
                {repo?.github_provisioning_error && (
                  <div className="col-span-full">
                    <div className="text-xs text-gray-500 mb-0.5">Last Error</div>
                    <div className="text-xs font-medium text-red-600 bg-red-50 rounded-lg px-3 py-1.5">{repo.github_provisioning_error}</div>
                  </div>
                )}
              </div>
            )}

            {!loadingRepo && !repo && (
              <div className="text-center py-6">
                <Github size={36} className="mx-auto mb-3 text-gray-300" />
                <p className="text-sm font-semibold text-gray-700 mb-1">No Repository Connected</p>
                <p className="text-xs text-gray-500 mb-4">Click "Set Up GitHub" above to create and connect your college's repository.</p>
              </div>
            )}
          </div>

          {/* ── Stats row ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <Stat label="Total Students"    value={students.length}   icon={Users}          color="#6366F1" />
            <Stat label="Pending Invites"   value={invited + ready}   icon={Clock}          color="#D97706" />
            <Stat label="Accepted"          value={accepted}          icon={CheckCircle2}   color="#059669" />
            <Stat label="Open PRs"          value={loadingAnalytics ? '…' : openPRs}  icon={GitPullRequest} color="#7C3AED"
              sub={loadingAnalytics ? undefined : `${mergedPRs} merged`} />
          </div>

          {/* ── Students table ── */}
          <div className="bg-white rounded-2xl mb-5" style={{ border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-xs)' }}>
            <div
              className="flex items-center justify-between px-5 py-4 cursor-pointer select-none"
              style={{ borderBottom: studentsExpanded ? '1px solid var(--border-subtle)' : 'none' }}
              onClick={() => setStudentsExpanded((v) => !v)}
            >
              <div className="flex items-center gap-2">
                <Users size={15} style={{ color: '#6366F1' }} />
                <h2 className="font-bold text-gray-900 text-sm">Connected Students</h2>
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-600">{students.length}</span>
              </div>
              <div className="flex items-center gap-2">
                {isSetUp && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSyncInvites(); }}
                    disabled={loadingSyncInv}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 disabled:opacity-60 transition"
                  >
                    {loadingSyncInv ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                    Sync Invites
                  </button>
                )}
                {studentsExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </div>
            </div>

            {studentsExpanded && (
              <div className="overflow-x-auto">
                {loadingStudents ? (
                  <div className="flex items-center justify-center py-10 gap-2 text-sm text-gray-400">
                    <Loader2 size={16} className="animate-spin" /> Loading students…
                  </div>
                ) : students.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-400">
                    No students with GitHub accounts yet.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Student</th>
                        <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">GitHub Username</th>
                        <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Invite Status</th>
                        <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Invited On</th>
                        <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Accepted On</th>
                        <th className="text-right text-xs font-semibold text-gray-500 px-5 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s) => {
                        const actLoading = studentActions[s.student_id];
                        const canInvite  = isSetUp && s.github_username && ['READY_FOR_INVITE', 'DECLINED'].includes(s.invite_status);
                        const canRevoke  = isSetUp && ['INVITED', 'ACCEPTED'].includes(s.invite_status);
                        return (
                          <tr key={s.student_id} style={{ borderBottom: '1px solid var(--border-subtle)' }} className="hover:bg-gray-50 transition">
                            <td className="px-5 py-3">
                              <div className="font-semibold text-gray-800">{s.full_name || '—'}</div>
                              <div className="text-xs text-gray-500">{s.email}</div>
                            </td>
                            <td className="px-5 py-3">
                              {s.github_username ? (
                                <a href={`https://github.com/${s.github_username}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline font-medium flex items-center gap-1">
                                  @{s.github_username}
                                  <ExternalLink size={10} />
                                </a>
                              ) : (
                                <span className="text-gray-400 text-xs">Not connected</span>
                              )}
                            </td>
                            <td className="px-5 py-3">
                              <InviteBadge status={s.invite_status} />
                            </td>
                            <td className="px-5 py-3 text-xs text-gray-500">{fmtDate(s.invited_at)}</td>
                            <td className="px-5 py-3 text-xs text-gray-500">{fmtDate(s.accepted_at)}</td>
                            <td className="px-5 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {canInvite && (
                                  <button
                                    onClick={() => handleInvite(s)}
                                    disabled={!!actLoading}
                                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-green-600 bg-green-50 hover:bg-green-100 disabled:opacity-60 transition"
                                  >
                                    {actLoading === 'invite' ? <Loader2 size={11} className="animate-spin" /> : <UserPlus size={11} />}
                                    Invite
                                  </button>
                                )}
                                {canRevoke && (
                                  <button
                                    onClick={() => handleRevoke(s)}
                                    disabled={!!actLoading}
                                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-60 transition"
                                  >
                                    {actLoading === 'revoke' ? <Loader2 size={11} className="animate-spin" /> : <UserMinus size={11} />}
                                    Revoke
                                  </button>
                                )}
                                {!canInvite && !canRevoke && (
                                  <span className="text-xs text-gray-400">—</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* ── Pull Requests ── */}
          <div className="bg-white rounded-2xl" style={{ border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-xs)' }}>
            <div
              className="flex items-center justify-between px-5 py-4 cursor-pointer select-none"
              style={{ borderBottom: prsExpanded ? '1px solid var(--border-subtle)' : 'none' }}
              onClick={() => setPrsExpanded((v) => !v)}
            >
              <div className="flex items-center gap-2">
                <GitPullRequest size={15} style={{ color: '#7C3AED' }} />
                <h2 className="font-bold text-gray-900 text-sm">Pull Requests</h2>
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-50 text-purple-600">
                  {openPRs} open · {mergedPRs} merged
                </span>
              </div>
              {prsExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
            </div>

            {prsExpanded && (
              <div className="overflow-x-auto">
                {loadingAnalytics ? (
                  <div className="flex items-center justify-center py-10 gap-2 text-sm text-gray-400">
                    <Loader2 size={16} className="animate-spin" /> Loading analytics…
                  </div>
                ) : prs.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-400">
                    No pull requests found.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Title</th>
                        <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Author</th>
                        <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">State</th>
                        <th className="text-left text-xs font-semibold text-gray-500 px-5 py-3">Created</th>
                        <th className="text-right text-xs font-semibold text-gray-500 px-5 py-3">Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prs.slice(0, 20).map((pr) => (
                        <tr key={pr.id || pr.number} style={{ borderBottom: '1px solid var(--border-subtle)' }} className="hover:bg-gray-50 transition">
                          <td className="px-5 py-3">
                            <div className="font-medium text-gray-800 max-w-xs truncate">{pr.title}</div>
                            <div className="text-xs text-gray-400">#{pr.number}</div>
                          </td>
                          <td className="px-5 py-3 text-gray-600">{pr.user?.login || pr.author || '—'}</td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${pr.state === 'open' ? 'bg-green-50 text-green-600' : pr.state === 'merged' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500'}`}>
                              {pr.state || '—'}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs text-gray-500">{fmtDate(pr.created_at)}</td>
                          <td className="px-5 py-3 text-right">
                            {pr.html_url ? (
                              <a href={pr.html_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline flex items-center justify-end gap-1">
                                <ExternalLink size={12} />
                              </a>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

        </main>
      </div>

      {/* ── Confirm dialog ── */}
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        danger={confirm?.danger}
        confirmLabel={confirm?.confirmLabel}
        loading={confirm?.loading}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
      <CollegeFooter />
      <style>{`
        footer[aria-label*="college Portal footer"] {
           transition: margin-left 300ms ease;
        }
      `}</style>
    </div>
  );
};

export default CollegeGitHubDashboard;
